import { useEffect, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { RoleId, UserDetail } from "@store/generalTypes";
import { isEmpty, uniq } from "lodash";

export const useUserPermission = ({ useCurrentLocation, locationId }: UseUserPermissionProps): UserPermission => {
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isClinicalAdmin, setIsClinicalAdmin] = useState(false);
    const [isFacilitator, setIsFacilitator] = useState(false);
    const [isClinician, setIsClinician] = useState(false);
    const [isResearcher, setIsResearcher] = useState(false);
    const [isDataQa, setIsDataQa] = useState(false);
    const [isClinicalStaff, setIsClinicalStaff] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [isOrganizationAdmin, setIsOrganizationAdmin] = useState(false);
    const [userRoles, setUserRoles] = useState<number[]>([]);

    const { userDetail, currentLocation } = useAppSelector((x) => {
        return { userDetail: x.userDetail, currentLocation: x.orgLocations.currentLocation };
    });

    useEffect(() => {
        setIsSuperAdmin(userDetail.isAdmin);
        setHasAccess(userDetail.hasAccess);

        let filterLocationId = locationId;

        if (useCurrentLocation) {
            filterLocationId = currentLocation?.id;
        }

        const orgId = userDetail.locations.find((x) => x.id === filterLocationId)?.organizationId;
        const organization = userDetail.organizations.find((x) => x.id === orgId) ?? userDetail.organizations[0];
        if (userDetail.hasAccess && organization) {
            let roles: number[] = organization.roles;

            setIsOrganizationAdmin(roles.includes(RoleId.Admin));

            roles = getUserRoles(userDetail, organization.roles, filterLocationId);

            setUserRoles(roles);
            setIsClinicalAdmin(roles.includes(RoleId.Admin));
            setIsFacilitator(roles.includes(RoleId.Facilitator));
            setIsClinician(roles.includes(RoleId.Clinician));
            setIsResearcher(roles.includes(RoleId.Researcher));
            setIsDataQa(roles.includes(RoleId.DataQA));
            setIsClinicalStaff(roles.includes(RoleId.ClinicalStaff));
        }
    }, [userDetail, locationId]);

    return {
        hasAccess,
        isSuperAdmin,
        isOrganizationAdmin,
        isClinicalAdmin,
        isFacilitator,
        isClinician,
        isResearcher,
        isDataQa,
        isClinicalStaff,
        userRoles
    };
};

export const getUserRoles = (
    userDetail: UserDetail,
    userRoles: number[] | undefined,
    locationId?: number
): number[] => {
    let roles = userRoles ? [...userRoles] : [];

    if (userDetail.isAdmin) roles.push(RoleId.SuperAdmin);

    if (!isEmpty(userDetail.locations)) {
        if (locationId) {
            const location = userDetail.locations.find((x) => x.id === locationId);

            if (location && !isEmpty(location.roles)) {
                if (location.roles.includes(RoleId.ClinicalStaff)) {
                    if (isEmpty(location.delegateUsers)) {
                        // staff user has clinicial delegate for all this location
                        roles.push(RoleId.Clinician);
                    } else {
                        // take roles from delegate user
                        const delegateRoles = location.delegateUsers.flatMap((x) => x.roles);
                        roles = [...roles, ...delegateRoles];
                    }
                }

                roles = [...roles, ...location.roles];
            }
        } else {
            if (userDetail.locations.some((x) => isEmpty(x.delegateUsers))) {
                const locationRoles = userDetail.locations.flatMap((x) => [
                    ...x.roles,
                    ...(x.delegateUsers?.flatMap((du) => du.roles) ?? [])
                ]);
                roles = [...roles, ...locationRoles];
            } else {
                roles = [...roles, RoleId.Clinician];
            }
        }
    }

    return uniq(roles);
};

interface UserPermission {
    hasAccess: boolean;
    isSuperAdmin: boolean;
    isOrganizationAdmin: boolean;
    isClinicalAdmin: boolean;
    isFacilitator: boolean;
    isClinician: boolean;
    isResearcher: boolean;
    isDataQa: boolean;
    isClinicalStaff: boolean;
    userRoles: number[];
}

interface UseUserPermissionProps {
    useCurrentLocation?: boolean;
    locationId?: number;
}
